import { PLATFORM_SPECS } from './platforms';
import {
  DEFAULT_GROWTH_SETTINGS,
  META_BID_STRATEGY_LABELS,
  type MetaAudienceRef,
  type MetaCtaType,
  type MetaGrowthSettings,
  type MetaLeadFormSpec,
  type Persona,
} from './types';
import { growthFormDefaults } from './lead-forms';

export type MetaDestination = 'website' | 'lead_form';

export interface DraftLeadForm {
  spec: MetaLeadFormSpec;
  /** The form it was duplicated from. */
  sourceId: string | null;
  /** Set once the form's name is edited by hand, so it stops following the program. */
  nameEdited: boolean;
}

export interface MetaDraft {
  /** The campaign name; follows Growth's convention from `program` until edited by hand. */
  name: string;
  /** What's promoted, e.g. "HCUC Ep15 Territory Planning"; the campaign, ad set and ad names build on it. */
  program: string;
  /** Who it's for; picking them pre-selects their audiences as targets. */
  personas: Persona[];
  /** '' until set; follows the convention like `name`. */
  adSetName: string;
  /** Set once the campaign or ad set name is edited by hand, so the convention stops filling them. */
  namesEdited: boolean;
  team: string;
  primaryText: string;
  headline: string;
  description: string;
  callToAction: MetaCtaType;
  destinationUrl: string;
  /** Where the ad sends people: a website, or an Instant Form (lead form) inside Facebook or Instagram. */
  destination: MetaDestination;
  /** An Instant Form on the Page; when set the ad opens it and destinationUrl is unused. */
  leadFormId: string | null;
  leadFormName: string | null;
  /**
   * The Instant Form to create at publish: Growth's standard one, or a duplicate. Null when the
   * request uses an existing form as it is (`leadFormId`), as requests from before forms did.
   */
  leadForm: DraftLeadForm | null;
  /** Targeting and bidding; only a reviewer's changes are saved. */
  growthSettings: MetaGrowthSettings;
  targets: MetaAudienceRef[];
  /** Set once the targets are changed by hand, so picking personas stops replacing them. */
  targetsEdited: boolean;
  /** Null until changed: Growth's default exclusions, resolved by name once the audiences load. */
  exclusions: MetaAudienceRef[] | null;
  /** Default exclusions a requester removed, each with why. Reviewers remove them without one. */
  droppedExclusions: { name: string; reason: string }[];
  dailyBudgetUsd: string;
  startDate: string;
  endDate: string;
}

export const EMPTY_META_DRAFT: MetaDraft = {
  name: '',
  program: '',
  personas: [],
  adSetName: '',
  namesEdited: false,
  team: '',
  primaryText: '',
  headline: '',
  description: '',
  callToAction: 'SIGN_UP',
  destinationUrl: '',
  destination: 'lead_form',
  leadFormId: null,
  leadFormName: null,
  leadForm: { spec: growthFormDefaults(''), sourceId: null, nameEdited: false },
  growthSettings: DEFAULT_GROWTH_SETTINGS,
  targets: [],
  targetsEdited: false,
  exclusions: null,
  droppedExclusions: [],
  dailyBudgetUsd: '500',
  startDate: '',
  endDate: '',
};

// A LinkedIn boost: an existing Clay company post, put into a copy of an existing campaign.
export interface LinkedInDraft {
  name: string;
  team: string;
  postUrl: string;
  campaignGroupId: string | null;
  campaignGroupName: string;
  sourceCampaignId: string | null;
  sourceCampaignName: string;
  dailyBudgetUsd: string;
  startDate: string;
  endDate: string;
}

export const EMPTY_LINKEDIN_DRAFT: LinkedInDraft = {
  name: '',
  team: '',
  postUrl: '',
  campaignGroupId: null,
  campaignGroupName: '',
  sourceCampaignId: null,
  sourceCampaignName: '',
  dailyBudgetUsd: '200',
  startDate: '',
  endDate: '',
};

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
/** "US" stays short; other codes read as their country name. */
export const countryName = (code: string) => (code === 'US' ? 'US' : (regionNames.of(code) ?? code));

/** What a Meta request publishes with: the objective follows the destination, the rest the settings. */
export function metaGrowthChips(destination: MetaDestination, s: MetaGrowthSettings): string[] {
  const bid =
    s.bidStrategy === 'COST_CAP' && s.bidAmountUsd !== null
      ? `${META_BID_STRATEGY_LABELS.COST_CAP}: $${s.bidAmountUsd}`
      : META_BID_STRATEGY_LABELS[s.bidStrategy];
  return [
    ...(destination === 'lead_form'
      ? ['Objective: leads (OUTCOME_LEADS)', 'Maximize number of leads (LEAD_GENERATION)']
      : ['Objective: traffic (OUTCOME_TRAFFIC)', 'Optimize for landing page views (LANDING_PAGE_VIEWS)']),
    bid,
    `Advantage+ audience ${s.advantageAudience ? 'on' : 'off'}`,
    `${s.countries.map(countryName).join(' + ')} · ${s.ageMin}–${s.ageMax}`,
    'Page: Clay',
    'Created paused',
  ];
}

export const LINKEDIN_GROWTH_DEFAULTS = [
  'Existing campaign group, never a new one',
  'Copies the chosen campaign: audience, exclusions, bid',
  'Audience expansion off',
  'Audience Network off',
  'Created as a draft',
];

export const META_PRIMARY_TEXT_VISIBLE_LIMIT = PLATFORM_SPECS.meta.creative.primaryTextVisibleLimit ?? 125;
export const META_HEADLINE_LIMIT = PLATFORM_SPECS.meta.creative.headlineLimit ?? 40;
